import { useLazyQuery } from "@apollo/client";
import {
  Box,
  Checkbox,
  FormControl,
  Grid,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
} from "@mui/material";
import { FETCH_TOPIC_LIST } from "@platformx/authoring-apis";
import {
  AutoTextArea,
  BasicSwitchText,
  CommonBoxWithNumber,
  TextBox,
  TitleSubTitle,
} from "@platformx/utilities";
import { XImageRender } from "@platformx/x-image-render";
import { useEffect, useState } from "react";
import { useCustomStyle } from "./FCMNotification.style";

const Notification = ({
  titleSubTitles,
  sectionNumber,
  state,
  setState,
  unsavedChanges,
  handleChange,
  contentRef,
  handleOnBlur,
}) => {
  // console.log("FCMNotification - Initial state:", state);
  const {
    sectionTitle,
    sectionSubTitle,
    notificationTitle,
    notificationSubTitle,
    notificationFieldName,

    notificationImageTitle,
    notificationImageSubTitle,
    notificationImageFieldName,

    mainTitle,
    mainSubTitle,
    titlePlaceHolder,
    titleMaxCharLength,
    titleFieldName,

    descriptionTitle,
    descriptionSubTitle,
    descriptionPlaceHolder,
    descriptionFieldName,
    descriptionMaxCharLength,

    channelTitle,
    channelSubTitle,
    channelFieldName,

    notificationTopic,
    notificationSubTopic,
    notificationTopicFieldName,
  } = titleSubTitles;

  const classes = useCustomStyle();
  const [runFetchTagList] = useLazyQuery(FETCH_TOPIC_LIST);

  // Local state for channel type selection
  const [selectedChannelTypes, setSelectedChannelTypes] = useState<string[]>([]);
  const [selectedNotificationTopic, setSelectedNotificationTopic] = useState<string[]>([]);
  const isVod = state?.CommonFields?.category === "Vod" ? true : false;

  const channelTypeOptions = [
    { value: "Web", label: "Web" },
    { value: "App", label: "App" },
  ];

  const [notificationTopicOptions, setNotificationTopicOptions] = useState<
    { value: string; label: string }[]
  >([]);

  useEffect(() => {
    runFetchTagList({
      variables: { start: 0, rows: 1000 },
    })
      .then((res) => {
        const topics = res?.data?.authoring_fetchTopics || [];
        setNotificationTopicOptions(
          topics.map((topic: any) => ({
            value: topic.label || topic.id || topic,
            label: topic.label || topic.id || topic,
          })),
        );
      })
      .catch((error: any) => {
        setNotificationTopicOptions([]);
        console.error("error in topic fetching", error);
      });
  }, [runFetchTagList]);

  //useEffect to sync local state with main state
  useEffect(() => {
    if (state?.[channelFieldName]) {
      setSelectedChannelTypes(state[channelFieldName]);
    }
    if (state?.[notificationTopicFieldName]) {
      setSelectedNotificationTopic(state[notificationTopicFieldName]);
    }
  }, [state, channelFieldName, notificationTopicFieldName]);
  const handleChannelTypeChange = (event) => {
    const { value } = event.target;
    const selectedValues = typeof value === "string" ? value.split(",") : value;

    setSelectedChannelTypes(selectedValues);
    handleDropdownChange({
      target: {
        name: channelFieldName,
        value: selectedValues,
      },
    });
  };

  const handleDropdownChange = (event) => {
    const { name, value } = event.target;
    contentRef.current = { ...contentRef.current, [name]: value };
    if (isVod) {
      setState((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            [name]: value,
          },
        };
      });
    } else {
      setState((prevState) => ({
        ...prevState,
        [name]: value,
      }));
    }

    unsavedChanges.current = true;
  };

  const handleNotificationTopicChange = (event) => {
    const { value } = event.target;
    let selectedValues = typeof value === "string" ? value.split(",") : value;

    // Handle "Select All" functionality
    if (selectedValues.includes("SELECT_ALL")) {
      const allValues = notificationTopicOptions.map((option) => option.value);
      const currentSelected = state?.[notificationTopicFieldName] || selectedNotificationTopic;

      // If all are selected, deselect all; otherwise select all
      selectedValues = currentSelected.length === allValues.length ? [] : allValues;
    }

    setSelectedNotificationTopic(selectedValues);
    handleDropdownChange({
      target: {
        name: notificationTopicFieldName,
        value: selectedValues,
      },
    });
  };
  const handleImageChange = (data) => {
    const { fieldName } = data;
    if (isVod) {
      setState((prevState) => {
        return {
          ...prevState,
          CommonFields: {
            ...prevState.CommonFields,
            [fieldName]: data?.original_image?.Thumbnail,
          },
        };
      });
    } else {
      setState((prevState) => {
        return {
          ...prevState,
          [fieldName]: data?.original_image?.Thumbnail,
        };
      });
    }

    contentRef.current = {
      ...contentRef.current,
      [fieldName]: data.relativeUrl,
    };
    unsavedChanges.current = true;
  };
  const getNotificationImageEditData = () => {
    if (isVod) {
      return {
        original_image: {
          original_image_relative_path:
            state?.CommonFields[notificationImageFieldName]?.split(".")?.length > 0
              ? state.CommonFields[notificationImageFieldName].split(".")[0]
              : "",
          ext:
            state?.CommonFields[notificationImageFieldName]?.split(".")?.length > 1
              ? `.${state?.CommonFields[notificationImageFieldName]?.split(".")[1]}`
              : "",
        },
      };
    } else {
      return {
        original_image: {
          original_image_relative_path:
            state?.[notificationImageFieldName]?.split(".")?.length > 0
              ? state[notificationImageFieldName].split(".")[0]
              : "",
          ext:
            state?.[notificationImageFieldName]?.split(".")?.length > 1
              ? `.${state?.[notificationImageFieldName]?.split(".")[1]}`
              : "",
        },
      };
    }
  };

  return (
    <Box id='Notification' className={classes.mainStyleWrapper}>
      <CommonBoxWithNumber
        number={sectionNumber}
        title={sectionTitle}
        titleVarient='h5bold'
        subTitleVarient='p4regular'
        subTitle={sectionSubTitle}>
        <Box className='textFiled'>
          <BasicSwitchText
            state={Boolean(
              isVod ? state?.CommonFields?.[notificationFieldName] : state?.[notificationFieldName],
            )}
            isDisable={false}
            handleChange={handleChange}
            title={notificationTitle}
            subtitle={notificationSubTitle}
            keyName={notificationFieldName}
          />
        </Box>
        <Grid container>
          <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
            <TitleSubTitle
              title={mainTitle}
              subTitle={mainSubTitle}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg={7} className='textFiled'>
            <TextBox
              name={titleFieldName}
              placeHolder={titlePlaceHolder}
              handleOnBlur={handleOnBlur}
              handleChange={handleChange}
              maxCharLength={titleMaxCharLength}
              state={
                isVod
                  ? (state?.CommonFields?.[titleFieldName] || "").substring(0, titleMaxCharLength)
                  : (state?.[titleFieldName] || state?.title || "").substring(0, titleMaxCharLength)
              }
            />
          </Grid>
          <Grid item xs={12} sm={5} md={5} className='leftFiled'>
            <TitleSubTitle
              title={descriptionTitle}
              subTitle={descriptionSubTitle}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={7} md={7} className='textFiled'>
            <AutoTextArea
              name={descriptionFieldName}
              placeHolder={descriptionPlaceHolder}
              handleOnBlur={handleOnBlur}
              handleChange={handleChange}
              maxCharLength={descriptionMaxCharLength}
              state={
                isVod
                  ? (state?.CommonFields?.[descriptionFieldName] || "").slice(
                      0,
                      descriptionMaxCharLength,
                    )
                  : (state?.[descriptionFieldName] || state?.description || "").slice(
                      0,
                      descriptionMaxCharLength,
                    )
              }
            />
          </Grid>
          <Grid item xs={12} sm={5} md={5} className='leftFiled'>
            <TitleSubTitle
              title={notificationImageTitle}
              subTitle={notificationImageSubTitle}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={7} md={7} className='textFiled'>
            <XImageRender
              callBack={(data) => {
                const callbackData = {
                  ...data,
                  fieldName: notificationImageFieldName,
                };
                handleImageChange(callbackData);
              }}
              editData={getNotificationImageEditData()}
              isCrop={false}
            />
          </Grid>
          <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
            <TitleSubTitle
              title={channelTitle}
              subTitle={channelSubTitle}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg={7} className='textFiled'>
            <FormControl fullWidth>
              <Select
                labelId='channel-type-label'
                id='channel-type-multiselect'
                multiple
                value={
                  isVod
                    ? Array.isArray(state?.CommonFields?.[channelFieldName])
                      ? state.CommonFields[channelFieldName]
                      : selectedChannelTypes
                    : Array.isArray(state?.[channelFieldName])
                      ? state[channelFieldName]
                      : selectedChannelTypes
                }
                onChange={handleChannelTypeChange}
                input={<OutlinedInput label={channelTitle} />}
                // renderValue={(selected) => (selected as string[]).join(", ")}
                renderValue={(selected) => (Array.isArray(selected) ? selected.join(", ") : "")}>
                {channelTypeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={
                        isVod
                          ? (
                              state?.CommonFields?.[channelFieldName] || selectedChannelTypes
                            ).includes(option.value)
                          : (state?.[channelFieldName] || selectedChannelTypes).includes(
                              option.value,
                            )
                      }
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={5} md={5} lg={5} className='leftFiled'>
            <TitleSubTitle
              title={notificationTopic}
              subTitle={notificationSubTopic}
              titleVariant='h6medium'
              subTitleVariant='p4regular'
            />
          </Grid>
          <Grid item xs={12} sm={12} md={7} lg={7} className='textFiled'>
            <FormControl fullWidth>
              <Select
                labelId='notification-topic-label'
                id='notification-topic-multiselect'
                multiple
                value={
                  isVod
                    ? Array.isArray(state?.CommonFields?.[notificationTopicFieldName])
                      ? state.CommonFields[notificationTopicFieldName]
                      : selectedNotificationTopic
                    : Array.isArray(state?.[notificationTopicFieldName])
                      ? state[notificationTopicFieldName]
                      : selectedNotificationTopic
                }
                onChange={handleNotificationTopicChange}
                displayEmpty
                input={<OutlinedInput label={notificationTopic} />}
                // renderValue={(selected) => (selected as string[]).join(", ")}
                renderValue={(selected) => (Array.isArray(selected) ? selected.join(", ") : "")}>
                {/* Select All Option */}
                <MenuItem value='SELECT_ALL'>
                  <Checkbox
                    checked={
                      notificationTopicOptions.length > 0 &&
                      (isVod
                        ? state?.CommonFields?.[notificationTopicFieldName] ||
                          selectedNotificationTopic
                        : state?.[notificationTopicFieldName] || selectedNotificationTopic
                      ).length === notificationTopicOptions.length
                    }
                    indeterminate={
                      (isVod
                        ? state?.CommonFields?.[notificationTopicFieldName] ||
                          selectedNotificationTopic
                        : state?.[notificationTopicFieldName] || selectedNotificationTopic
                      ).length > 0 &&
                      (isVod
                        ? state?.CommonFields?.[notificationTopicFieldName] ||
                          selectedNotificationTopic
                        : state?.[notificationTopicFieldName] || selectedNotificationTopic
                      ).length < notificationTopicOptions.length
                    }
                  />
                  <ListItemText primary='Select All' />
                </MenuItem>

                {notificationTopicOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    <Checkbox
                      checked={(isVod
                        ? state?.CommonFields?.[notificationTopicFieldName] ||
                          selectedNotificationTopic
                        : state?.[notificationTopicFieldName] || selectedNotificationTopic
                      ).includes(option.value)}
                    />
                    <ListItemText primary={option.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CommonBoxWithNumber>
    </Box>
  );
};
export default Notification;
